import Content from '@/services/Content';

/** 64 MB — suitable for dashboard feed uploads (AWS minimum part size is 5 MB). */
export const UPLOAD_CHUNK_SIZE = 64 * 1024 * 1024;

/** 1 GB — used by the video editor for very large source files. */
export const UPLOAD_CHUNK_SIZE_LARGE = 1024 * 1024 * 1024;

function formatUploadError(error) {
    if (!error) {
        return 'Upload failed';
    }

    const body = error.response?.data;
    if (body?.error != null) {
        if (Array.isArray(body.error)) {
            return body.error.join(' ');
        }
        if (typeof body.error === 'object') {
            return Object.values(body.error).flat().join(' ');
        }
        return String(body.error);
    }

    if (body?.message) {
        return String(body.message);
    }

    if (typeof error === 'string') {
        return error;
    }

    if (error.message) {
        // Browser/CORS failures against S3 often look like Network Error.
        if (/network error/i.test(error.message) || error.code === 'ERR_NETWORK') {
            return 'Network/CORS error while uploading to storage. Check S3 CORS for this origin.';
        }
        return error.message;
    }

    return 'Upload failed';
}

/**
 * Uppy AwsS3Multipart callbacks. Sends upload_from so the API picks the S3 bucket.
 *
 * @param {string} [uploadFrom='editor'] 'videos' | 'shorts' | 'editor'
 */
export function createUppyS3MultipartHandlers(uploadFrom = 'editor') {
    const resolveUploadFrom = (file) => file?.meta?.uploadFrom || uploadFrom;

    const withUploadFrom = (file, payload) => ({
        ...payload,
        upload_from: resolveUploadFrom(file),
    });

    return {
        async createMultipartUpload(file) {
            try {
                file.meta = { ...(file.meta || {}), uploadFrom };

                const response = await Content.createMultipartUpload(withUploadFrom(file, {
                    filename: file.name,
                    content_type: file.type || 'video/mp4',
                }));

                const payload = response?.data || {};
                if (!payload.upload_id || !payload.key) {
                    throw new Error(formatUploadError({ response }) || 'Invalid createMultipartUpload response');
                }

                return { uploadId: payload.upload_id, key: payload.key };
            } catch (error) {
                throw new Error(formatUploadError(error));
            }
        },

        async signPart(file, partData) {
            try {
                const response = await Content.signPart(withUploadFrom(file, {
                    upload_id: partData.uploadId,
                    key: partData.key,
                    part_number: partData.partNumber,
                }));

                const url = response?.data?.url;
                if (!url) {
                    throw new Error(formatUploadError({ response }) || 'Invalid signPart response');
                }

                return { url };
            } catch (error) {
                throw new Error(formatUploadError(error));
            }
        },

        async completeMultipartUpload(file, { uploadId, key, parts }) {
            try {
                const response = await Content.completeMultipartUpload(withUploadFrom(file, {
                    upload_id: uploadId,
                    key,
                    parts: parts.map((p) => ({
                        PartNumber: p.PartNumber ?? p.partNumber,
                        ETag: p.ETag ?? p.eTag ?? p.etag ?? null,
                    })).filter((p) => p.PartNumber != null),
                }));

                return { location: response?.data?.location || null };
            } catch (error) {
                throw new Error(formatUploadError(error));
            }
        },

        async abortMultipartUpload(file, partData) {
            try {
                await Content.abortMultipartUpload(withUploadFrom(file, {
                    upload_id: partData.uploadId,
                    key: partData.key,
                }));
            } catch (error) {
                // Abort is best-effort; surface only if nothing else reported failure.
                throw new Error(formatUploadError(error));
            }
        },
    };
}

/**
 * Upload a single File via Uppy + S3 multipart. Resolves with the S3 object key.
 *
 * @param {import('@uppy/core').Uppy} uppy
 * @param {File} file
 * @returns {Promise<string>}
 */
export function uploadFileWithUppy(uppy, file) {
    return new Promise((resolve, reject) => {
        uppy.getFiles().forEach((f) => uppy.removeFile(f.id));

        let settled = false;

        const finish = (fn, value) => {
            if (settled) return;
            settled = true;
            cleanup();
            fn(value);
        };

        const onComplete = (result) => {
            if (result.successful?.length) {
                const key = result.successful[0]?.s3Multipart?.key;
                if (key) {
                    finish(resolve, key);
                    return;
                }
            }

            const failed = result.failed?.[0];
            const failedMsg = formatUploadError(failed?.error || failed);
            finish(reject, new Error(failedMsg || 'Upload failed'));
        };

        const onError = (fileObj, error) => {
            finish(reject, new Error(formatUploadError(error)));
        };

        const cleanup = () => {
            uppy.off('complete', onComplete);
            uppy.off('upload-error', onError);
        };

        uppy.on('complete', onComplete);
        uppy.on('upload-error', onError);

        try {
            uppy.addFile({
                name: file.name,
                type: file.type || 'video/mp4',
                data: file,
            });
        } catch (error) {
            finish(reject, new Error(formatUploadError(error)));
            return;
        }

        uppy.upload().catch((error) => {
            finish(reject, new Error(formatUploadError(error)));
        });
    });
}
